import { useEffect, useState, useRef, useCallback, createContext, useContext } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useNavigate, useLocation, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Toaster, toast } from "sonner";
import axios from "axios";
import { 
  Shield, 
  GitBranch, 
  Lock, 
  Database, 
  ChevronRight, 
  Plus, 
  Trash2, 
  Download, 
  RefreshCw,
  LogOut,
  Home,
  FolderGit2,
  Archive,
  User,
  Eye,
  EyeOff,
  Check,
  Clock,
  HardDrive,
  Layers,
  Zap,
  ExternalLink,
  Menu,
  XIcon,
  ArrowRightLeft,
  Cloud,
  ShieldCheck,
  Activity,
  Copy,
  Building2,
  KeyRound,
  Settings
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper to extract error message from API response
const getErrorMessage = (error, fallback = "An error occurred") => {
  const detail = error?.response?.data?.detail;
  if (!detail) return fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map(e => e.msg || e.message || String(e)).join(', ');
  }
  if (typeof detail === 'object') {
    return detail.msg || detail.message || JSON.stringify(detail);
  }
  return fallback;
};

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

// Setup axios to use token from localStorage
axios.interceptors.request.use((config) => {
  const token = localStorage.getItem('codevault_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }

    const token = localStorage.getItem('codevault_token');
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch (error) {
      localStorage.removeItem('codevault_token');
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {});
    } catch (error) {
      console.error("Logout error:", error);
    }
    localStorage.removeItem('codevault_token');
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
};

// Auth Callback Component
const AuthCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { setUser } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      const hash = location.hash;
      const sessionIdMatch = hash.match(/session_id=([^&]+)/);
      
      if (!sessionIdMatch) {
        navigate("/");
        return;
      }

      const sessionId = sessionIdMatch[1];

      try {
        const response = await axios.post(
          `${API}/auth/session`,
          { session_id: sessionId }
        );
        
        setUser(response.data);
        navigate("/dashboard", { replace: true, state: { user: response.data } });
      } catch (error) {
        console.error("Auth error:", error);
        toast.error("Authentication failed");
        navigate("/");
      }
    };

    processAuth();
  }, [location, navigate, setUser]);

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center">
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center">
        <div className="w-12 h-12 border-2 border-[#00F0FF] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
        <p className="text-zinc-400 font-mono text-sm">AUTHENTICATING...</p>
      </motion.div>
    </div>
  );
};

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!loading && !user && !location.state?.user) {
      navigate("/");
    }
  }, [user, loading, navigate, location]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="w-12 h-12 border-2 border-[#00F0FF] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!user && !location.state?.user) return null;

  return children;
};

// Matrix Rain Effect Component
const MatrixRain = () => {
  const canvasRef = useRef(null);
  
  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    
    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);
    
    const chars = 'アイウエオカキクケコサシスセソタチツテトナニヌネノハヒフヘホマミムメモヤユヨラリルレロワヲン0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz{}[]()<>/*-+=%$#@!&^~`;:,.?|\\\'\"';
    const charArray = chars.split('');
    const fontSize = 14;
    const columns = Math.floor(canvas.width / fontSize);
    const drops = Array(columns).fill(1);
    
    const draw = () => {
      ctx.fillStyle = 'rgba(0, 0, 0, 0.05)';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      ctx.fillStyle = '#00FF94';
      ctx.font = `${fontSize}px monospace`;
      
      for (let i = 0; i < drops.length; i++) {
        const char = charArray[Math.floor(Math.random() * charArray.length)];
        const x = i * fontSize;
        const y = drops[i] * fontSize;
        
        // Vary the color intensity
        const alpha = Math.random() * 0.5 + 0.5;
        ctx.fillStyle = `rgba(0, 255, 148, ${alpha})`;
        ctx.fillText(char, x, y);
        
        // Head of the stream is brighter
        if (drops[i] * fontSize < canvas.height && Math.random() > 0.95) {
          ctx.fillStyle = '#FFFFFF';
          ctx.fillText(char, x, y);
        }
        
        if (y > canvas.height && Math.random() > 0.975) {
          drops[i] = 0;
        }
        drops[i]++;
      }
    };
    
    const interval = setInterval(draw, 35);
    
    return () => {
      clearInterval(interval);
      window.removeEventListener('resize', resizeCanvas);
    };
  }, []);
  
  return (
    <canvas
      ref={canvasRef}
      className="fixed inset-0 z-0"
      style={{ background: '#000000' }}
    />
  );
};

// Landing Page
const LandingPage = () => {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [totpCode, setTotpCode] = useState("");
  const [needsTotp, setNeedsTotp] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [logging, setLogging] = useState(false);

  useEffect(() => {
    if (user) navigate("/dashboard");
  }, [user, navigate]);

  const handleDirectLogin = async (e) => {
    e.preventDefault();
    setLogging(true);
    try {
      const response = await axios.post(`${API}/auth/login`, {
        email,
        password,
        totp_code: totpCode || undefined
      }, {});
      
      // Save token to localStorage for subsequent requests
      if (response.data.token) {
        localStorage.setItem('codevault_token', response.data.token);
      }
      
      setUser(response.data.user);
      navigate("/dashboard");
    } catch (error) {
      if (error.response?.status === 403 && error.response?.data?.detail === "TOTP code required") {
        setNeedsTotp(true);
        toast.info("Enter your authenticator code");
      } else {
        toast.error(getErrorMessage(error, "Login failed"));
      }
    } finally {
      setLogging(false);
    }
  };

  return (
    <div className="min-h-screen relative overflow-hidden">
      <MatrixRain />
      
      {/* Scanline overlay */}
      <div className="fixed inset-0 z-10 pointer-events-none opacity-30" style={{
        background: 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(0,0,0,0.3) 2px, rgba(0,0,0,0.3) 4px)'
      }} />
      
      {/* Main Content */}
      <div className="relative z-20 min-h-screen flex flex-col items-center justify-center px-6">
        <motion.div 
          initial={{ opacity: 0, y: 20 }} 
          animate={{ opacity: 1, y: 0 }} 
          transition={{ duration: 0.8 }}
          className="w-full max-w-md"
        >
          {/* Title */}
          <div className="text-center mb-12">
            <motion.h1 
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="font-mono text-4xl sm:text-5xl font-bold text-[#00FF94] tracking-tight mb-2"
              style={{ textShadow: '0 0 20px rgba(0, 255, 148, 0.5), 0 0 40px rgba(0, 255, 148, 0.3)' }}
            >
              CODEVAULT
            </motion.h1>
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="h-px w-32 mx-auto bg-gradient-to-r from-transparent via-[#00FF94] to-transparent"
            />
          </div>

          {/* Login Form */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="backdrop-blur-sm bg-black/60 border border-[#00FF94]/20 p-8"
            style={{ boxShadow: '0 0 30px rgba(0, 255, 148, 0.1)' }}
          >
            <form onSubmit={handleDirectLogin} className="space-y-6">
              <div>
                <Input
                  data-testid="login-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="EMAIL"
                  className="bg-black/80 border-[#00FF94]/30 text-[#00FF94] placeholder:text-[#00FF94]/40 font-mono text-sm h-12 focus:border-[#00FF94] focus:ring-[#00FF94]/20"
                  required
                />
              </div>
              
              <div className="relative">
                <Input
                  data-testid="login-password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="PASSWORD"
                  className="bg-black/80 border-[#00FF94]/30 text-[#00FF94] placeholder:text-[#00FF94]/40 font-mono text-sm h-12 pr-12 focus:border-[#00FF94] focus:ring-[#00FF94]/20"
                  required
                />
                <button 
                  type="button" 
                  onClick={() => setShowPassword(!showPassword)} 
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-[#00FF94]/50 hover:text-[#00FF94] transition-colors"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>

              <AnimatePresence>
                {needsTotp && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                  >
                    <Input
                      data-testid="login-totp"
                      type="text"
                      value={totpCode}
                      onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      placeholder="2FA CODE"
                      className="bg-black/80 border-[#00FF94]/30 text-[#00FF94] placeholder:text-[#00FF94]/40 font-mono text-lg h-12 text-center tracking-[0.5em] focus:border-[#00FF94] focus:ring-[#00FF94]/20"
                      maxLength={6}
                      autoFocus
                    />
                  </motion.div>
                )}
              </AnimatePresence>

              <Button
                data-testid="login-submit"
                type="submit"
                disabled={logging}
                className="w-full h-12 bg-[#00FF94] text-black font-mono text-sm tracking-wider hover:bg-[#00FF94]/90 transition-all border-0"
                style={{ boxShadow: logging ? 'none' : '0 0 20px rgba(0, 255, 148, 0.4)' }}
              >
                {logging ? (
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin" />
                    <span>AUTHENTICATING</span>
                  </div>
                ) : needsTotp ? (
                  "VERIFY"
                ) : (
                  "ACCESS"
                )}
              </Button>
            </form>
          </motion.div>
        </motion.div>
      </div>
    </div>
  );
};

// Subtle Matrix Rain for Dashboard (lighter version)
const DashboardMatrixRain = () => {
  const canvasRef = useRef(null);
  
  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    
    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);
    
    const chars = '01';
    const charArray = chars.split('');
    const fontSize = 12;
    const columns = Math.floor(canvas.width / fontSize);
    const drops = Array(columns).fill(1);
    
    const draw = () => {
      ctx.fillStyle = 'rgba(0, 0, 0, 0.03)';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      ctx.font = `${fontSize}px monospace`;
      
      for (let i = 0; i < drops.length; i++) {
        if (Math.random() > 0.98) {
          const char = charArray[Math.floor(Math.random() * charArray.length)];
          const x = i * fontSize;
          const y = drops[i] * fontSize;
          
          ctx.fillStyle = `rgba(0, 255, 148, ${Math.random() * 0.15 + 0.05})`;
          ctx.fillText(char, x, y);
          
          if (y > canvas.height && Math.random() > 0.99) {
            drops[i] = 0;
          }
          drops[i]++;
        }
      }
    };
    
    const interval = setInterval(draw, 50);
    
    return () => {
      clearInterval(interval);
      window.removeEventListener('resize', resizeCanvas);
    };
  }, []);
  
  return (
    <canvas
      ref={canvasRef}
      className="fixed inset-0 z-0 pointer-events-none"
      style={{ background: 'transparent' }}
    />
  );
};

// Dashboard Layout Component
const DashboardLayout = ({ children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const navItems = [
    { path: "/dashboard", label: "HOME" },
    { path: "/accounts", label: "ACCOUNTS" },
    { path: "/repositories", label: "REPOS" },
    { path: "/vault", label: "VAULT" },
    { path: "/settings", label: "2FA" },
  ];

  return (
    <div className="min-h-screen bg-black relative">
      <DashboardMatrixRain />
      
      {/* Scanline overlay */}
      <div className="fixed inset-0 z-10 pointer-events-none opacity-20" style={{
        background: 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(0,0,0,0.3) 2px, rgba(0,0,0,0.3) 4px)'
      }} />
      
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-black/80 backdrop-blur-sm border-b border-[#00FF94]/20">
        <div className="px-4 md:px-6 py-3 flex items-center justify-between">
          <Link to="/dashboard" className="flex items-center gap-2">
            <span className="font-mono font-bold text-lg text-[#00FF94]" style={{ textShadow: '0 0 10px rgba(0, 255, 148, 0.5)' }}>CODEVAULT</span>
          </Link>

          <div className="hidden md:flex items-center gap-1">
            {navItems.map((item) => (
              <Link 
                key={item.path} 
                to={item.path} 
                className={`px-4 py-2 text-xs font-mono tracking-wider transition-all ${
                  location.pathname === item.path 
                    ? "text-[#00FF94] bg-[#00FF94]/10 border border-[#00FF94]/30" 
                    : "text-[#00FF94]/50 hover:text-[#00FF94] hover:bg-[#00FF94]/5"
                }`}
              >
                {item.label}
              </Link>
            ))}
          </div>

          <div className="flex items-center gap-3">
            <Button 
              data-testid="logout-btn" 
              variant="ghost" 
              onClick={handleLogout}
              className="text-[#00FF94]/50 hover:text-[#00FF94] hover:bg-[#00FF94]/10 font-mono text-xs"
            >
              LOGOUT
            </Button>

            <Button data-testid="mobile-menu-btn" variant="ghost" size="icon" className="md:hidden text-[#00FF94]" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
              {mobileMenuOpen ? <XIcon className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </Button>
          </div>
        </div>

        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="md:hidden border-t border-[#00FF94]/20 bg-black/95">
              <div className="px-4 py-2">
                {navItems.map((item) => (
                  <Link 
                    key={item.path} 
                    to={item.path} 
                    onClick={() => setMobileMenuOpen(false)} 
                    className={`block px-4 py-3 text-sm font-mono ${location.pathname === item.path ? "text-[#00FF94]" : "text-[#00FF94]/50"}`}
                  >
                    {item.label}
                  </Link>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>

      <main className="relative z-20 pt-16 min-h-screen">
        <div className="p-4 md:p-6 lg:p-8 max-w-6xl mx-auto">{children}</div>
      </main>
    </div>
  );
};

// Format bytes utility
const formatBytes = (bytes) => {
  if (!bytes || bytes === 0) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
};

// Dashboard Page
const DashboardPage = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchStats(); }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/stats`, {});
      setStats(response.data);
    } catch (error) {
      toast.error("Failed to load statistics");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-[#00FF94] font-mono text-sm animate-pulse">LOADING...</div>
      </div>
    );
  }

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} data-testid="dashboard-page">
      {/* Terminal Header */}
      <div className="mb-8 border border-[#00FF94]/20 bg-black/50 p-4">
        <div className="font-mono text-xs text-[#00FF94]/50 mb-2">root@codevault:~$</div>
        <h1 className="font-mono text-2xl text-[#00FF94]" style={{ textShadow: '0 0 10px rgba(0, 255, 148, 0.3)' }}>
          SYSTEM STATUS
        </h1>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {[
          { label: "ACCOUNTS", value: stats?.connected_accounts || 0 },
          { label: "LIVE_SYNC", value: stats?.sync_repos || 0 },
          { label: "VAULTED", value: stats?.vault_repos || 0 },
          { label: "SNAPSHOTS", value: stats?.total_vault_entries || 0 }
        ].map((stat, i) => (
          <motion.div 
            key={i} 
            initial={{ opacity: 0, y: 10 }} 
            animate={{ opacity: 1, y: 0 }} 
            transition={{ delay: i * 0.1 }}
            className="border border-[#00FF94]/20 bg-black/50 p-4 hover:border-[#00FF94]/40 transition-colors"
          >
            <div className="font-mono text-3xl text-[#00FF94] mb-1" style={{ textShadow: '0 0 10px rgba(0, 255, 148, 0.3)' }}>
              {stat.value}
            </div>
            <div className="font-mono text-xs text-[#00FF94]/50">{stat.label}</div>
          </motion.div>
        ))}
      </div>

      {/* Storage Stats */}
      <div className="grid md:grid-cols-2 gap-4 mb-8">
        <div className="border border-[#00FF94]/20 bg-black/50 p-4">
          <div className="font-mono text-xs text-[#00FF94]/50 mb-2">LIVE_SYNC_STORAGE</div>
          <div className="font-mono text-2xl text-[#00FF94]">{formatBytes(stats?.sync_storage_bytes)}</div>
        </div>
        <div className="border border-[#00FF94]/20 bg-black/50 p-4">
          <div className="font-mono text-xs text-[#00FF94]/50 mb-2">ENCRYPTED_VAULT</div>
          <div className="font-mono text-2xl text-[#00FF94]">{formatBytes(stats?.vault_storage_bytes)}</div>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="grid md:grid-cols-2 gap-4">
        {/* Recent Syncs */}
        <div className="border border-[#00FF94]/20 bg-black/50 p-4">
          <div className="font-mono text-xs text-[#00FF94]/50 mb-4">RECENT_SYNCS</div>
          {stats?.recent_syncs?.length > 0 ? (
            <div className="space-y-2">
              {stats.recent_syncs.map((sync, i) => (
                <div key={i} className="font-mono text-sm border-l-2 border-[#00FF94]/30 pl-3 py-1">
                  <div className="text-[#00FF94]">{sync.repo_full_name}</div>
                  <div className="text-[#00FF94]/40 text-xs">{sync.current_commit?.slice(0, 7)}</div>
                </div>
              ))}
            </div>
          ) : (
            <div className="font-mono text-sm text-[#00FF94]/30">NO_DATA</div>
          )}
        </div>

        {/* Recent Vault Entries */}
        <div className="border border-[#00FF94]/20 bg-black/50 p-4">
          <div className="font-mono text-xs text-[#00FF94]/50 mb-4">RECENT_VAULT_ENTRIES</div>
          {stats?.recent_vaults?.length > 0 ? (
            <div className="space-y-2">
              {stats.recent_vaults.map((vault, i) => (
                <div key={i} className="font-mono text-sm border-l-2 border-[#00FF94]/30 pl-3 py-1">
                  <div className="text-[#00FF94]">{vault.repo_full_name}</div>
                  <div className="text-[#00FF94]/40 text-xs">{vault.commit_sha?.slice(0, 7)} • {formatBytes(vault.total_size_bytes)}</div>
                </div>
              ))}
            </div>
          ) : (
            <div className="font-mono text-sm text-[#00FF94]/30">NO_DATA</div>
          )}
        </div>
      </div>
    </motion.div>
  );
};

// Accounts Page
const AccountsPage = () => {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [pat, setPat] = useState("");
  const [showPat, setShowPat] = useState(false);
  const [connecting, setConnecting] = useState(false);

  useEffect(() => { fetchAccounts(); }, []);

  const fetchAccounts = async () => {
    try {
      const response = await axios.get(`${API}/github/accounts`, {});
      setAccounts(response.data);
    } catch (error) {
      toast.error("Failed to load accounts");
    } finally {
      setLoading(false);
    }
  };

  const connectAccount = async () => {
    if (!pat.trim()) {
      toast.error("Please enter a Personal Access Token");
      return;
    }

    setConnecting(true);
    try {
      const response = await axios.post(`${API}/github/connect`, { personal_access_token: pat }, {});
      setAccounts([...accounts, response.data]);
      const ownerType = response.data.resource_owner_type === 'Organization' ? 'ORG' : 'USER';
      toast.success(`CONNECTED: @${response.data.resource_owner} [${ownerType}]`);
      setDialogOpen(false);
      setPat("");
    } catch (error) {
      toast.error(getErrorMessage(error, "Failed to connect account"));
    } finally {
      setConnecting(false);
    }
  };

  const disconnectAccount = async (accountId, resourceOwner) => {
    try {
      await axios.delete(`${API}/github/accounts/${accountId}`, {});
      setAccounts(accounts.filter(a => a.account_id !== accountId));
      toast.success(`DISCONNECTED: @${resourceOwner}`);
    } catch (error) {
      toast.error("Failed to disconnect account");
    }
  };

  const copyWebhookUrl = (accountId) => {
    const url = `${BACKEND_URL}/api/webhook/${accountId}`;
    navigator.clipboard.writeText(url);
    toast.success("WEBHOOK_URL COPIED");
  };

  const personalAccounts = accounts.filter(a => a.resource_owner_type !== 'Organization');
  const orgAccounts = accounts.filter(a => a.resource_owner_type === 'Organization');

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} data-testid="accounts-page">
      {/* Terminal Header */}
      <div className="mb-8 border border-[#00FF94]/20 bg-black/50 p-4">
        <div className="font-mono text-xs text-[#00FF94]/50 mb-2">root@codevault:~$ github --accounts</div>
        <div className="flex items-center justify-between">
          <h1 className="font-mono text-2xl text-[#00FF94]" style={{ textShadow: '0 0 10px rgba(0, 255, 148, 0.3)' }}>
            GITHUB ACCOUNTS
          </h1>
          <Button 
            data-testid="connect-account-btn" 
            onClick={() => setDialogOpen(true)}
            className="bg-[#00FF94] text-black font-mono text-xs hover:bg-[#00FF94]/90"
          >
            + ADD_TOKEN
          </Button>
        </div>
      </div>

      {/* Add Token Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-black border-[#00FF94]/30">
          <DialogHeader>
            <DialogTitle className="font-mono text-[#00FF94]">ADD_GITHUB_TOKEN</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="relative">
              <Input 
                data-testid="pat-input" 
                type={showPat ? "text" : "password"} 
                placeholder="github_pat_xxxxxxxxxxxx" 
                value={pat} 
                onChange={(e) => setPat(e.target.value)} 
                className="bg-black border-[#00FF94]/30 text-[#00FF94] font-mono pr-10 focus:border-[#00FF94]" 
              />
              <button type="button" onClick={() => setShowPat(!showPat)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#00FF94]/50 hover:text-[#00FF94]">
                {showPat ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            <div className="border border-[#00FF94]/20 bg-black/50 p-3 font-mono text-xs">
              <p className="text-[#00FF94]/50">CREATE_TOKEN_AT:</p>
              <a href="https://github.com/settings/personal-access-tokens/new" target="_blank" rel="noopener noreferrer" className="text-[#00FF94] hover:underline flex items-center gap-1 mt-1">
                github.com/settings/personal-access-tokens
                <ExternalLink className="w-3 h-3" />
              </a>
            </div>
            <Button 
              data-testid="confirm-connect-btn" 
              onClick={connectAccount} 
              disabled={connecting} 
              className="w-full bg-[#00FF94] text-black font-mono hover:bg-[#00FF94]/90"
            >
              {connecting ? 'CONNECTING...' : 'CONNECT'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="text-[#00FF94] font-mono text-sm animate-pulse">LOADING...</div>
        </div>
      ) : accounts.length > 0 ? (
        <div className="space-y-6">
          {/* Personal Accounts */}
          {personalAccounts.length > 0 && (
            <div>
              <div className="font-mono text-xs text-[#00FF94]/50 mb-3">PERSONAL_ACCOUNTS ({personalAccounts.length})</div>
              <div className="space-y-2">
                {personalAccounts.map((account) => (
                  <AccountCard 
                    key={account.account_id} 
                    account={account} 
                    onCopyWebhook={copyWebhookUrl}
                    onDisconnect={disconnectAccount}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Organizations */}
          {orgAccounts.length > 0 && (
            <div>
              <div className="font-mono text-xs text-[#00FF94]/50 mb-3">ORGANIZATIONS ({orgAccounts.length})</div>
              <div className="space-y-2">
                {orgAccounts.map((account) => (
                  <AccountCard 
                    key={account.account_id} 
                    account={account} 
                    onCopyWebhook={copyWebhookUrl}
                    onDisconnect={disconnectAccount}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="border border-[#00FF94]/20 bg-black/50 p-12 text-center">
          <div className="font-mono text-[#00FF94]/30 text-sm mb-4">NO_ACCOUNTS_CONNECTED</div>
          <Button 
            data-testid="connect-first-account-btn" 
            onClick={() => setDialogOpen(true)} 
            className="bg-[#00FF94] text-black font-mono text-xs hover:bg-[#00FF94]/90"
          >
            + ADD_TOKEN
          </Button>
        </div>
      )}
    </motion.div>
  );
};

// Account Card Component
const AccountCard = ({ account, onCopyWebhook, onDisconnect }) => {
  const isOrg = account.resource_owner_type === 'Organization';
  const resourceOwner = account.resource_owner || account.github_username;
  
  return (
    <div className="border border-[#00FF94]/20 bg-black/50 p-4 hover:border-[#00FF94]/40 transition-colors">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-[#00FF94]/10 flex items-center justify-center">
            {account.avatar_url ? (
              <img src={account.avatar_url} alt="" className="w-full h-full object-cover" />
            ) : (
              <User className="w-5 h-5 text-[#00FF94]" />
            )}
          </div>
          <div>
            <div className="font-mono text-[#00FF94]">@{resourceOwner}</div>
            <div className="font-mono text-xs text-[#00FF94]/40">
              [{isOrg ? 'ORG' : 'USER'}] {new Date(account.connected_at).toLocaleDateString()}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button 
            data-testid={`copy-webhook-${resourceOwner}`} 
            variant="outline" 
            size="sm" 
            className="border-[#00FF94]/30 text-[#00FF94] hover:bg-[#00FF94]/10 font-mono text-xs" 
            onClick={() => onCopyWebhook(account.account_id)}
          >
            WEBHOOK
          </Button>
          <Link to={`/repositories?account=${account.account_id}`}>
            <Button 
              data-testid={`view-repos-${resourceOwner}`} 
              size="sm" 
              className="bg-[#00FF94] text-black hover:bg-[#00FF94]/90 font-mono text-xs"
            >
              REPOS
            </Button>
          </Link>
          <Button 
            data-testid={`disconnect-${resourceOwner}`} 
            variant="ghost" 
            size="sm" 
            className="text-red-400 hover:text-red-300 hover:bg-red-400/10 font-mono text-xs" 
            onClick={() => onDisconnect(account.account_id, resourceOwner)}
          >
            DELETE
          </Button>
        </div>
      </div>
    </div>
  );
};

// Repositories Page
const RepositoriesPage = () => {
  const [repos, setRepos] = useState([]);
  const [githubRepos, setGithubRepos] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fetchingRepos, setFetchingRepos] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const accountId = params.get('account');
    if (accountId) {
      setSelectedAccount(accountId);
      setDialogOpen(true);
    }
    fetchData();
  }, [location]);

  const fetchData = async () => {
    try {
      const [reposRes, accountsRes] = await Promise.all([
        axios.get(`${API}/repositories`),
        axios.get(`${API}/github/accounts`)
      ]);
      setRepos(reposRes.data);
      setAccounts(accountsRes.data);
    } catch (error) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const fetchGithubRepos = async (accountId) => {
    setFetchingRepos(true);
    try {
      const response = await axios.get(`${API}/github/accounts/${accountId}/repos`, {});
      setGithubRepos(response.data);
    } catch (error) {
      toast.error("Failed to fetch repositories");
    } finally {
      setFetchingRepos(false);
    }
  };

  useEffect(() => {
    if (selectedAccount && dialogOpen) {
      fetchGithubRepos(selectedAccount);
    }
  }, [selectedAccount, dialogOpen]);

  const addRepository = async (accountId, repoId) => {
    try {
      await axios.post(`${API}/repositories/${accountId}/${repoId}`, {}, {});
      toast.success("REPO_ADDED - SYNCING...");
      setGithubRepos(githubRepos.map(r => r.github_repo_id === repoId ? { ...r, is_monitored: true } : r));
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error, "Failed to add repository"));
    }
  };

  const removeRepository = async (repoId) => {
    try {
      await axios.delete(`${API}/repositories/${repoId}`, {});
      setRepos(repos.filter(r => r.repo_id !== repoId));
      toast.success("REPO_REMOVED");
    } catch (error) {
      toast.error("Failed to remove repository");
    }
  };

  const changeMode = async (repoId, newMode) => {
    toast.loading(`MOVING_TO_${newMode.toUpperCase()}...`, { id: repoId });
    try {
      await axios.patch(`${API}/repositories/${repoId}/mode`, { storage_mode: newMode }, {});
      toast.success(`MOVED_TO_${newMode.toUpperCase()}`, { id: repoId });
      fetchData();
    } catch (error) {
      toast.error("MODE_CHANGE_FAILED", { id: repoId });
    }
  };

  const triggerSync = async (repoId) => {
    toast.loading("SYNCING...", { id: repoId });
    try {
      const response = await axios.post(`${API}/sync/${repoId}`, {}, {});
      toast.success(`SYNCED: ${response.data.commit_sha?.slice(0, 7)}`, { id: repoId });
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error, "SYNC_FAILED"), { id: repoId });
    }
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} data-testid="repositories-page">
      {/* Terminal Header */}
      <div className="mb-8 border border-[#00FF94]/20 bg-black/50 p-4">
        <div className="font-mono text-xs text-[#00FF94]/50 mb-2">root@codevault:~$ repos --list</div>
        <div className="flex items-center justify-between">
          <h1 className="font-mono text-2xl text-[#00FF94]" style={{ textShadow: '0 0 10px rgba(0, 255, 148, 0.3)' }}>
            LIVE_SYNC REPOS
          </h1>
          <Button 
            data-testid="add-repository-btn" 
            onClick={() => setDialogOpen(true)}
            disabled={accounts.length === 0}
            className="bg-[#00FF94] text-black font-mono text-xs hover:bg-[#00FF94]/90"
          >
            + ADD_REPO
          </Button>
        </div>
      </div>

      {/* Add Repository Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-black border-[#00FF94]/30 max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-mono text-[#00FF94]">SELECT_REPOSITORY</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            {/* Account Selection */}
            <div>
              <div className="font-mono text-xs text-[#00FF94]/50 mb-2">RESOURCE_OWNER</div>
              <div className="flex gap-2 flex-wrap">
                {accounts.map((account) => {
                  const resourceOwner = account.resource_owner || account.github_username;
                  return (
                    <Button 
                      key={account.account_id} 
                      data-testid={`select-account-${resourceOwner}`} 
                      size="sm" 
                      onClick={() => { setSelectedAccount(account.account_id); setGithubRepos([]); }} 
                      className={selectedAccount === account.account_id 
                        ? "bg-[#00FF94] text-black font-mono text-xs" 
                        : "bg-transparent border border-[#00FF94]/30 text-[#00FF94] font-mono text-xs hover:bg-[#00FF94]/10"}
                    >
                      @{resourceOwner}
                    </Button>
                  );
                })}
              </div>
            </div>
            
            {fetchingRepos ? (
              <div className="flex items-center justify-center py-12">
                <div className="text-[#00FF94] font-mono text-sm animate-pulse">FETCHING...</div>
              </div>
            ) : githubRepos.length > 0 ? (
              <ScrollArea className="h-[350px] pr-4">
                <div className="space-y-2">
                  {githubRepos.map((repo) => (
                    <div key={repo.github_repo_id} className="flex items-center justify-between p-3 border border-[#00FF94]/20 bg-black/50 hover:border-[#00FF94]/40 transition-colors">
                      <div className="min-w-0">
                        <div className="font-mono text-sm text-[#00FF94]">{repo.full_name}</div>
                        <div className="font-mono text-xs text-[#00FF94]/40">
                          {repo.private ? '[PRIVATE]' : '[PUBLIC]'} {repo.language && `• ${repo.language}`}
                        </div>
                      </div>
                      <Button 
                        data-testid={`add-repo-${repo.name}`} 
                        size="sm" 
                        disabled={repo.is_monitored} 
                        onClick={() => addRepository(selectedAccount, repo.github_repo_id)} 
                        className={repo.is_monitored 
                          ? "bg-[#00FF94]/20 text-[#00FF94] font-mono text-xs" 
                          : "bg-[#00FF94] text-black font-mono text-xs"}
                      >
                        {repo.is_monitored ? 'ADDED' : 'ADD'}
                      </Button>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            ) : selectedAccount ? (
              <div className="text-center py-12 font-mono text-sm text-[#00FF94]/30">NO_REPOS_FOUND</div>
            ) : (
              <div className="text-center py-12 font-mono text-sm text-[#00FF94]/30">SELECT_ACCOUNT</div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="text-[#00FF94] font-mono text-sm animate-pulse">LOADING...</div>
        </div>
      ) : repos.length > 0 ? (
        <div className="space-y-2">
          <div className="font-mono text-xs text-[#00FF94]/50 mb-4">ACTIVE_REPOS ({repos.length})</div>
          {repos.map((repo) => (
            <div key={repo.repo_id} className="border border-[#00FF94]/20 bg-black/50 p-4 hover:border-[#00FF94]/40 transition-colors">
              <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                <div className="min-w-0">
                  <div className="font-mono text-[#00FF94]">{repo.full_name}</div>
                  <div className="font-mono text-xs text-[#00FF94]/40 mt-1">
                    {repo.sync_info && (
                      <>COMMIT: {repo.sync_info.current_commit?.slice(0, 7)} • SIZE: {formatBytes(repo.sync_info.total_size_bytes)}</>
                    )}
                    {repo.last_sync && <> • LAST_SYNC: {new Date(repo.last_sync).toLocaleDateString()}</>}
                  </div>
                </div>
                
                <div className="flex items-center gap-2 flex-wrap">
                  <Button 
                    data-testid={`sync-${repo.repo_id}`} 
                    size="sm" 
                    variant="outline" 
                    className="border-[#00FF94]/30 text-[#00FF94] hover:bg-[#00FF94]/10 font-mono text-xs" 
                    onClick={() => triggerSync(repo.repo_id)}
                  >
                    SYNC
                  </Button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button 
                        data-testid={`move-to-vault-${repo.repo_id}`} 
                        size="sm" 
                        className="bg-[#00FF94] text-black hover:bg-[#00FF94]/90 font-mono text-xs"
                      >
                        VAULT
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent className="bg-black border-[#00FF94]/30">
                      <AlertDialogHeader>
                        <AlertDialogTitle className="font-mono text-[#00FF94]">MOVE_TO_VAULT?</AlertDialogTitle>
                        <AlertDialogDescription className="font-mono text-[#00FF94]/50 text-sm">
                          {repo.full_name} will be encrypted and moved to cold storage.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel className="bg-transparent border-[#00FF94]/30 text-[#00FF94] font-mono text-xs">CANCEL</AlertDialogCancel>
                        <AlertDialogAction className="bg-[#00FF94] text-black font-mono text-xs" onClick={() => changeMode(repo.repo_id, 'vault')}>CONFIRM</AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                  <Button 
                    data-testid={`remove-${repo.repo_id}`} 
                    size="sm" 
                    variant="ghost" 
                    className="text-red-400 hover:text-red-300 hover:bg-red-400/10 font-mono text-xs" 
                    onClick={() => removeRepository(repo.repo_id)}
                  >
                    DELETE
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="border border-[#00FF94]/20 bg-black/50 p-12 text-center">
          <div className="font-mono text-[#00FF94]/30 text-sm mb-4">NO_REPOS_SYNCED</div>
          {accounts.length > 0 ? (
            <Button 
              data-testid="add-first-repo-btn" 
              onClick={() => setDialogOpen(true)} 
              className="bg-[#00FF94] text-black font-mono text-xs hover:bg-[#00FF94]/90"
            >
              + ADD_REPO
            </Button>
          ) : (
            <Link to="/accounts">
              <Button className="bg-[#00FF94] text-black font-mono text-xs hover:bg-[#00FF94]/90">
                CONNECT_GITHUB
              </Button>
            </Link>
          )}
        </div>
      )}
    </motion.div>
  );
};

// Vault Page
const VaultPage = () => {
  const [vaultData, setVaultData] = useState({ entries: [], vaulted_repos: [] });
  const [loading, setLoading] = useState(true);
  const [selectedRepo, setSelectedRepo] = useState(null);

  useEffect(() => { fetchVault(); }, []);

  const fetchVault = async () => {
    try {
      const response = await axios.get(`${API}/vault`, {});
      setVaultData(response.data);
    } catch (error) {
      toast.error("Failed to load vault");
    } finally {
      setLoading(false);
    }
  };

  const downloadEntry = async (vaultId, commitSha) => {
    toast.loading("DECRYPTING...", { id: vaultId });
    try {
      const response = await axios.get(`${API}/vault/${vaultId}/download`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${commitSha.slice(0, 8)}.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("DOWNLOAD_COMPLETE", { id: vaultId });
    } catch (error) {
      toast.error("DOWNLOAD_FAILED", { id: vaultId });
    }
  };

  const deleteEntry = async (vaultId) => {
    try {
      await axios.delete(`${API}/vault/${vaultId}`, {});
      setVaultData(prev => ({
        ...prev,
        entries: prev.entries.filter(e => e.vault_id !== vaultId)
      }));
      toast.success("ENTRY_DELETED");
    } catch (error) {
      toast.error("DELETE_FAILED");
    }
  };

  const restoreToSync = async (repoId) => {
    toast.loading("RESTORING...", { id: repoId });
    try {
      await axios.patch(`${API}/repositories/${repoId}/mode`, { storage_mode: "sync" }, {});
      toast.success("RESTORED_TO_SYNC", { id: repoId });
      fetchVault();
    } catch (error) {
      toast.error("RESTORE_FAILED", { id: repoId });
    }
  };

  const createNewVaultEntry = async (repoId) => {
    toast.loading("CREATING_SNAPSHOT...", { id: repoId });
    try {
      await axios.post(`${API}/vault/${repoId}`, {}, {});
      toast.success("SNAPSHOT_CREATED", { id: repoId });
      fetchVault();
    } catch (error) {
      toast.error(getErrorMessage(error, "SNAPSHOT_FAILED"), { id: repoId });
    }
  };

  const entriesByRepo = vaultData.entries.reduce((acc, entry) => {
    if (!acc[entry.repo_id]) acc[entry.repo_id] = [];
    acc[entry.repo_id].push(entry);
    return acc;
  }, {});

  const filteredEntries = selectedRepo 
    ? vaultData.entries.filter(e => e.repo_id === selectedRepo)
    : vaultData.entries;

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} data-testid="vault-page">
      {/* Terminal Header */}
      <div className="mb-8 border border-[#00FF94]/20 bg-black/50 p-4">
        <div className="font-mono text-xs text-[#00FF94]/50 mb-2">root@codevault:~$ vault --encrypted</div>
        <h1 className="font-mono text-2xl text-[#00FF94]" style={{ textShadow: '0 0 10px rgba(0, 255, 148, 0.3)' }}>
          ENCRYPTED VAULT
        </h1>
      </div>

      {/* Encryption Info */}
      <div className="border border-[#00FF94]/20 bg-black/50 p-4 mb-6">
        <div className="font-mono text-xs text-[#00FF94]/50 mb-2">ENCRYPTION_SPEC</div>
        <div className="font-mono text-sm text-[#00FF94]">AES-256-GCM + ChaCha20-Poly1305</div>
        <div className="font-mono text-xs text-[#00FF94]/40 mt-1">600K PBKDF2 • AIR_GAPPED_KEYS</div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="text-[#00FF94] font-mono text-sm animate-pulse">LOADING...</div>
        </div>
      ) : (vaultData.vaulted_repos?.length > 0 || vaultData.entries?.length > 0) ? (
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Vaulted Projects List */}
          <div className="lg:col-span-1">
            <div className="font-mono text-xs text-[#00FF94]/50 mb-3">VAULTED_PROJECTS ({vaultData.vaulted_repos?.length || 0})</div>
            <div className="space-y-2">
              <button 
                onClick={() => setSelectedRepo(null)}
                className={`w-full text-left p-3 border font-mono text-sm transition-colors ${
                  selectedRepo === null 
                    ? 'border-[#00FF94] bg-[#00FF94]/10 text-[#00FF94]' 
                    : 'border-[#00FF94]/20 bg-black/50 text-[#00FF94]/70 hover:border-[#00FF94]/40'
                }`}
              >
                ALL_SNAPSHOTS ({vaultData.entries?.length || 0})
              </button>
              {vaultData.vaulted_repos?.map((repo) => {
                const entryCount = entriesByRepo[repo.repo_id]?.length || 0;
                return (
                  <div 
                    key={repo.repo_id} 
                    onClick={() => setSelectedRepo(repo.repo_id)}
                    className={`p-3 border cursor-pointer transition-colors ${
                      selectedRepo === repo.repo_id 
                        ? 'border-[#00FF94] bg-[#00FF94]/10' 
                        : 'border-[#00FF94]/20 bg-black/50 hover:border-[#00FF94]/40'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="min-w-0 flex-1">
                        <div className="font-mono text-sm text-[#00FF94] truncate">{repo.full_name}</div>
                        <div className="font-mono text-xs text-[#00FF94]/40">{entryCount} SNAPSHOT{entryCount !== 1 ? 'S' : ''}</div>
                      </div>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                          <Button variant="ghost" size="sm" className="text-[#00FF94]/50 hover:text-[#00FF94] h-8 w-8 p-0">
                            <ChevronRight className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="bg-black border-[#00FF94]/30">
                          <DropdownMenuItem 
                            onClick={(e) => { e.stopPropagation(); createNewVaultEntry(repo.repo_id); }} 
                            className="cursor-pointer font-mono text-xs text-[#00FF94]"
                          >
                            NEW_SNAPSHOT
                          </DropdownMenuItem>
                          <DropdownMenuItem 
                            onClick={(e) => { e.stopPropagation(); restoreToSync(repo.repo_id); }} 
                            className="cursor-pointer font-mono text-xs text-[#00FF94]"
                          >
                            RESTORE_TO_SYNC
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Snapshots List */}
          <div className="lg:col-span-2">
            <div className="font-mono text-xs text-[#00FF94]/50 mb-3">
              {selectedRepo ? 'SNAPSHOTS' : `ALL_SNAPSHOTS (${filteredEntries.length})`}
            </div>
            {filteredEntries.length > 0 ? (
              <div className="space-y-2">
                {filteredEntries.map((entry) => (
                  <div key={entry.vault_id} className="border border-[#00FF94]/20 bg-black/50 p-4 hover:border-[#00FF94]/40 transition-colors">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-mono text-[#00FF94]">{entry.commit_sha?.slice(0, 7)}</span>
                          {!selectedRepo && <span className="font-mono text-sm text-[#00FF94]/50 truncate">{entry.repo_full_name}</span>}
                        </div>
                        <div className="font-mono text-xs text-[#00FF94]/40 truncate">{entry.commit_message}</div>
                        <div className="font-mono text-xs text-[#00FF94]/30 mt-1">
                          {entry.commit_author} • {new Date(entry.created_at).toLocaleDateString()} • {formatBytes(entry.total_size_bytes)}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <Button 
                          data-testid={`download-${entry.vault_id}`} 
                          size="sm" 
                          className="bg-[#00FF94] text-black hover:bg-[#00FF94]/90 font-mono text-xs" 
                          onClick={() => downloadEntry(entry.vault_id, entry.commit_sha)}
                        >
                          DOWNLOAD
                        </Button>
                        <Button 
                          data-testid={`delete-${entry.vault_id}`} 
                          size="sm" 
                          variant="ghost" 
                          className="text-red-400 hover:bg-red-400/10 font-mono text-xs" 
                          onClick={() => deleteEntry(entry.vault_id)}
                        >
                          DELETE
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="border border-[#00FF94]/20 bg-black/50 p-12 text-center">
                <div className="font-mono text-[#00FF94]/30 text-sm">NO_SNAPSHOTS</div>
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="border border-[#00FF94]/20 bg-black/50 p-12 text-center">
          <div className="font-mono text-[#00FF94]/30 text-sm mb-4">VAULT_EMPTY</div>
          <Link to="/repositories">
            <Button className="bg-[#00FF94] text-black font-mono text-xs hover:bg-[#00FF94]/90">
              VIEW_REPOS
            </Button>
          </Link>
        </div>
      )}
    </motion.div>
  );
};

// Settings Page
const SettingsPage = () => {
  const { user } = useAuth();
  const [totpStatus, setTotpStatus] = useState({ totp_enabled: false });
  const [setupData, setSetupData] = useState(null);
  const [verifyCode, setVerifyCode] = useState("");
  const [disableCode, setDisableCode] = useState("");
  const [loading, setLoading] = useState(true);
  const [settingUp, setSettingUp] = useState(false);

  useEffect(() => {
    fetchTotpStatus();
  }, []);

  const fetchTotpStatus = async () => {
    try {
      const response = await axios.get(`${API}/auth/totp/status`, {});
      setTotpStatus(response.data);
    } catch (error) {
      console.error("Failed to fetch TOTP status");
    } finally {
      setLoading(false);
    }
  };

  const startTotpSetup = async () => {
    setSettingUp(true);
    try {
      const response = await axios.post(`${API}/auth/totp/setup`, {}, {});
      setSetupData(response.data);
    } catch (error) {
      toast.error("Failed to start TOTP setup");
    } finally {
      setSettingUp(false);
    }
  };

  const verifyAndEnable = async () => {
    if (verifyCode.length !== 6) {
      toast.error("Enter 6-digit code");
      return;
    }
    try {
      await axios.post(`${API}/auth/totp/verify`, { code: verifyCode }, {});
      toast.success("2FA ENABLED");
      setSetupData(null);
      setVerifyCode("");
      fetchTotpStatus();
    } catch (error) {
      toast.error(getErrorMessage(error, "Invalid code"));
    }
  };

  const disableTotp = async () => {
    if (disableCode.length !== 6) {
      toast.error("Enter 6-digit code to disable");
      return;
    }
    try {
      await axios.post(`${API}/auth/totp/disable`, { code: disableCode }, {});
      toast.success("2FA DISABLED");
      setDisableCode("");
      fetchTotpStatus();
    } catch (error) {
      toast.error(getErrorMessage(error, "Invalid code"));
    }
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} data-testid="settings-page">
      {/* Terminal Header */}
      <div className="mb-8 border border-[#00FF94]/20 bg-black/50 p-4">
        <div className="font-mono text-xs text-[#00FF94]/50 mb-2">root@codevault:~$ security --2fa</div>
        <h1 className="font-mono text-2xl text-[#00FF94]" style={{ textShadow: '0 0 10px rgba(0, 255, 148, 0.3)' }}>
          TWO-FACTOR AUTH
        </h1>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="text-[#00FF94] font-mono text-sm animate-pulse">LOADING...</div>
        </div>
      ) : (
        <div className="max-w-xl">
          {/* Current Status */}
          <div className="border border-[#00FF94]/20 bg-black/50 p-6 mb-6">
            <div className="font-mono text-xs text-[#00FF94]/50 mb-4">STATUS</div>
            <div className={`font-mono text-lg ${totpStatus.totp_enabled ? 'text-[#00FF94]' : 'text-red-400'}`}>
              {totpStatus.totp_enabled ? '[ ENABLED ]' : '[ DISABLED ]'}
            </div>
          </div>

          {totpStatus.totp_enabled ? (
            /* Disable Section */
            <div className="border border-[#00FF94]/20 bg-black/50 p-6">
              <div className="font-mono text-xs text-[#00FF94]/50 mb-4">DISABLE_2FA</div>
              <p className="font-mono text-sm text-[#00FF94]/70 mb-4">
                Enter code from authenticator to disable:
              </p>
              <div className="flex gap-3">
                <Input
                  data-testid="disable-totp-code"
                  type="text"
                  value={disableCode}
                  onChange={(e) => setDisableCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="000000"
                  className="bg-black border-[#00FF94]/30 text-[#00FF94] font-mono text-xl text-center tracking-[0.3em] max-w-40 focus:border-[#00FF94]"
                  maxLength={6}
                />
                <Button
                  data-testid="disable-totp-btn"
                  onClick={disableTotp}
                  className="bg-red-500/20 text-red-400 border border-red-500/30 hover:bg-red-500/30 font-mono"
                >
                  DISABLE
                </Button>
              </div>
            </div>
          ) : setupData ? (
            /* Setup Flow */
            <div className="border border-[#00FF94]/20 bg-black/50 p-6 space-y-6">
              <div className="font-mono text-xs text-[#00FF94]/50">SETUP_2FA</div>
              
              {/* QR Code */}
              <div className="text-center">
                <p className="font-mono text-sm text-[#00FF94]/70 mb-4">
                  Scan QR code with your authenticator app:
                </p>
                <div className="inline-block p-4 bg-white">
                  <img 
                    src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(setupData.provisioning_uri)}`}
                    alt="TOTP QR Code"
                    className="w-48 h-48"
                  />
                </div>
              </div>

              {/* Manual Entry */}
              <div className="border-t border-[#00FF94]/20 pt-4">
                <p className="font-mono text-xs text-[#00FF94]/50 mb-2">MANUAL_ENTRY</p>
                <code className="block font-mono text-sm text-[#00FF94] bg-black/50 p-3 border border-[#00FF94]/20 break-all">
                  {setupData.secret}
                </code>
              </div>

              {/* Verify */}
              <div className="border-t border-[#00FF94]/20 pt-4">
                <p className="font-mono text-sm text-[#00FF94]/70 mb-3">
                  Enter code from app to verify:
                </p>
                <div className="flex gap-3">
                  <Input
                    data-testid="verify-totp-code"
                    type="text"
                    value={verifyCode}
                    onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="000000"
                    className="bg-black border-[#00FF94]/30 text-[#00FF94] font-mono text-xl text-center tracking-[0.3em] max-w-40 focus:border-[#00FF94]"
                    maxLength={6}
                    autoFocus
                  />
                  <Button
                    data-testid="verify-totp-btn"
                    onClick={verifyAndEnable}
                    className="bg-[#00FF94] text-black hover:bg-[#00FF94]/90 font-mono"
                  >
                    VERIFY
                  </Button>
                </div>
              </div>

              <Button 
                variant="ghost" 
                onClick={() => setSetupData(null)} 
                className="text-[#00FF94]/50 hover:text-[#00FF94] font-mono"
              >
                CANCEL
              </Button>
            </div>
          ) : (
            /* Enable Button */
            <div className="border border-[#00FF94]/20 bg-black/50 p-6">
              <div className="font-mono text-xs text-[#00FF94]/50 mb-4">ENABLE_2FA</div>
              <p className="font-mono text-sm text-[#00FF94]/70 mb-4">
                Add extra security with TOTP authenticator
              </p>
              <Button
                data-testid="setup-totp-btn"
                onClick={startTotpSetup}
                disabled={settingUp}
                className="bg-[#00FF94] text-black hover:bg-[#00FF94]/90 font-mono"
              >
                {settingUp ? 'GENERATING...' : 'SETUP 2FA'}
              </Button>
            </div>
          )}
        </div>
      )}
    </motion.div>
  );
};

// App Router
function AppRouter() {
  const location = useLocation();
  
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/dashboard" element={<ProtectedRoute><DashboardLayout><DashboardPage /></DashboardLayout></ProtectedRoute>} />
      <Route path="/accounts" element={<ProtectedRoute><DashboardLayout><AccountsPage /></DashboardLayout></ProtectedRoute>} />
      <Route path="/repositories" element={<ProtectedRoute><DashboardLayout><RepositoriesPage /></DashboardLayout></ProtectedRoute>} />
      <Route path="/vault" element={<ProtectedRoute><DashboardLayout><VaultPage /></DashboardLayout></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute><DashboardLayout><SettingsPage /></DashboardLayout></ProtectedRoute>} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Toaster position="top-right" toastOptions={{ style: { background: '#0A0A0A', border: '1px solid rgba(255,255,255,0.1)', color: '#fff' } }} />
        <AppRouter />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
